// Folder picker modal used when moving a file via the "Taşı" button.

import { useMemo, useState } from 'react';
import { Modal, TreeSelect, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { foldersApi, type FolderTreeNode } from '@api/folders';

const { Text } = Typography;

const ROOT_VALUE = '__root__';

interface TreeSelectNode {
  title: string;
  value: string;
  children?: TreeSelectNode[];
}

function toTreeSelectData(nodes: FolderTreeNode[]): TreeSelectNode[] {
  return nodes.map((n) => ({
    title: n.name,
    value: n.id,
    children: n.children.length ? toTreeSelectData(n.children) : undefined,
  }));
}

interface MoveFileModalProps {
  open: boolean;
  currentFolderId: string | null;
  onCancel: () => void;
  onConfirm: (targetFolderId: string | null) => void;
}

export function MoveFileModal({
  open,
  currentFolderId,
  onCancel,
  onConfirm,
}: MoveFileModalProps) {
  const { t } = useTranslation('media');
  const [value, setValue] = useState<string | undefined>(
    currentFolderId ?? ROOT_VALUE,
  );

  const { data: tree = [] } = useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: () => foldersApi.tree(),
    enabled: open,
    staleTime: 60_000,
  });

  const treeData: TreeSelectNode[] = useMemo(
    () => [
      {
        title: t('moveModal.rootOption'),
        value: ROOT_VALUE,
        children: toTreeSelectData(tree),
      },
    ],
    [tree, t],
  );

  return (
    <Modal
      open={open}
      title={t('moveModal.title')}
      onCancel={onCancel}
      onOk={() => onConfirm(!value || value === ROOT_VALUE ? null : value)}
      okText={t('moveModal.okText')}
      cancelText={t('moveModal.cancelText')}
      destroyOnHidden
    >
      <Text>{t('moveModal.targetLabel')}</Text>
      <TreeSelect
        style={{ width: '100%', marginTop: 8 }}
        treeData={treeData}
        treeDefaultExpandAll
        value={value}
        onChange={(v) => setValue(v)}
        placeholder={t('moveModal.placeholder')}
        data-testid="move-file-target"
      />
    </Modal>
  );
}
